import { useState, useRef, useCallback, useEffect } from 'react';
import { message } from 'antd';
import store from '@/reducers';
import useAuth from '@/hooks/useAuth';
import {
  textToSpeech,
  downloadAudio,
  createAudioUrl,
  revokeAudioUrl,
} from '@/pages/common/qing-hua/zheng-ming-ze/40/tts';
import {
  mapPitchToSemitones,
  mapSpeechRateToTempo,
  renderProcessedAudioBlob,
} from '@/pages/common/qing-hua/zheng-ming-ze/40/audioTransform';

export function useQwenTTS() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const { checkLogin } = useAuth();

  const audioRef = useRef(null);
  const rawBlobRef = useRef(null);
  const rawUrlRef = useRef(null);
  const rawSigRef = useRef('');
  const procBlobRef = useRef(null);
  const procUrlRef = useRef(null);
  const procSigRef = useRef('');
  const taskRef = useRef(0);

  const clearRaw = useCallback(() => {
    if (rawUrlRef.current) revokeAudioUrl(rawUrlRef.current);
    rawBlobRef.current = null;
    rawUrlRef.current = null;
    rawSigRef.current = '';
  }, []);

  const clearProc = useCallback(() => {
    if (procUrlRef.current) revokeAudioUrl(procUrlRef.current);
    procBlobRef.current = null;
    procUrlRef.current = null;
    procSigRef.current = '';
  }, []);

  const ensureProcessed = useCallback(
    async (text, voice, languageType, speechRate, pitch) => {
      const sourceSig = ['qwen3-tts-flash', text, voice, languageType].join(
        '::',
      );

      if (!rawBlobRef.current || rawSigRef.current !== sourceSig) {
        return null;
      }

      if (speechRate === 0 && pitch === 0 && rawUrlRef.current) {
        clearProc();
        setAudioUrl(rawUrlRef.current);
        return rawUrlRef.current;
      }

      const processedSig = [sourceSig, speechRate, pitch].join('::');
      if (
        procBlobRef.current &&
        procSigRef.current === processedSig &&
        procUrlRef.current
      ) {
        setAudioUrl(procUrlRef.current);
        return procUrlRef.current;
      }

      const taskId = ++taskRef.current;
      setIsProcessingAudio(true);

      try {
        const processed = await renderProcessedAudioBlob({
          audioBlob: rawBlobRef.current,
          tempo: mapSpeechRateToTempo(speechRate),
          pitchSemitones: mapPitchToSemitones(pitch),
        });

        if (taskRef.current !== taskId) return null;

        const url = createAudioUrl(processed);
        clearProc();
        procSigRef.current = processedSig;
        procBlobRef.current = processed;
        procUrlRef.current = url;
        setAudioUrl(url);
        return url;
      } catch (err) {
        console.error('音频处理失败:', JSON.stringify(err));
        if (rawUrlRef.current) {
          setAudioUrl(rawUrlRef.current);
          return rawUrlRef.current;
        }
        return null;
      } finally {
        if (taskRef.current === taskId) setIsProcessingAudio(false);
      }
    },
    [clearProc],
  );

  const generate = useCallback(
    async (text, voice, languageType, speechRate, pitch) => {
      const uid = store.getState().users.uid;
      if (!uid) return;
      if (!text.trim()) return;
      if (text.length > 100) return;

      const sourceSig = ['qwen3-tts-flash', text, voice, languageType].join(
        '::',
      );

      setIsGenerating(true);
      try {
        const shouldRequestApi =
          rawSigRef.current !== sourceSig ||
          !rawBlobRef.current ||
          !rawUrlRef.current;

        if (shouldRequestApi) {
          clearProc();
          const resp = await textToSpeech({
            text,
            voice,
            language_type: languageType,
            model: 'qwen3-tts-flash',
          });

          const audioUrlFromApi =
            resp?.output?.audio?.url ||
            resp?.data?.url ||
            resp?.data?.audio?.url ||
            resp?.output?.audio?.data;

          if (!audioUrlFromApi) {
            throw new Error('API响应格式错误');
          }

          message.success('音频生成成功');
          message.open({
            content: '消耗1积分',
            className: 'costSuccess',
          });
          checkLogin();

          const blob = await downloadAudio(audioUrlFromApi);
          const url = createAudioUrl(blob);
          clearRaw();
          rawSigRef.current = sourceSig;
          rawBlobRef.current = blob;
          rawUrlRef.current = url;
        }

        const playable = await ensureProcessed(
          text,
          voice,
          languageType,
          speechRate,
          pitch,
        );
        if (playable) {
          setAudioUrl(playable);
          setIsPlaying(false);
        }
      } catch (err) {
        console.error('生成失败:', JSON.stringify(err));
      } finally {
        setIsGenerating(false);
      }
    },
    [clearRaw, clearProc, ensureProcessed, checkLogin],
  );

  const play = useCallback(async () => {
    if (!audioRef.current || !audioUrl) return;
    try {
      if (isPlaying && !isPaused) {
        audioRef.current.pause();
      } else if (isPaused) {
        await audioRef.current.play();
        setIsPaused(false);
        setIsPlaying(true);
      } else {
        if (audioRef.current.src !== audioUrl) {
          audioRef.current.src = audioUrl;
        }
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        setIsPlaying(true);
        setIsPaused(false);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        alert('请先点击页面任意位置，然后再次点击播放按钮');
      }
    }
  }, [audioUrl, isPlaying, isPaused]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const handlePause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleDownload = useCallback(() => {
    if (!procBlobRef.current && !rawBlobRef.current) return;
    try {
      const blob = procBlobRef.current || rawBlobRef.current;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `语音合成_${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下载失败:', JSON.stringify(err));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (rawUrlRef.current) revokeAudioUrl(rawUrlRef.current);
      if (procUrlRef.current) revokeAudioUrl(procUrlRef.current);
    };
  }, []);

  return {
    isGenerating,
    isProcessingAudio,
    isPlaying,
    isPaused,
    audioUrl,
    audioRef,
    generate,
    play,
    stop,
    handleEnded,
    handlePause,
    handleDownload,
  };
}
